
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';
import DownloadExcel from 'vue-json-excel/JsonExcel.vue';

// --- Models ---
import { Load, LoadEvent, ExtraSummary } from '../models/Load.model';

// --- Services ---
import AppService from '@/services/app';
import LoadEventService from '@/services/loadEvent';
import LoadExtraService from '@/services/loadExtra';
import LoadService from '@/services/load';
import UserService from '@/services/user';

// --- Third Party ---
import dayjs from 'dayjs';
import Hashids from 'hashids';

@Component({
  components: {
    DownloadExcel,
  },
})
export default class ApprovalListing extends Vue {
  hashids = new Hashids(process.env.VUE_APP_HASHIDS_SALT);

  // used by Filter Area
  selectedStatuses: number[] = [];
  search: string = '';

  // used by No Data Slot
  isLoading: boolean = true;

  clickedItem: Load = Object.assign({}, this.$store.getters['loads/emptyLoad']);
  expandDelay: boolean = false;
  loadExtraSummary: ExtraSummary[] = [];
  loadEvents: LoadEvent[] = [];

  // used by Update Order Number Dialog
  showOrderNumberDialog: boolean = false;
  newOrderNumber: string = '';
  orderNumberValid: boolean = false;
  orderNumberClicked: boolean = false;

  // used by Confirm Action Dialog for Approve/Reject
  action: string = '';
  dialogContent: string = '';
  confirmActionDelay: boolean = false;
  showConfirmActionDialog: boolean = false;
  rejectionReason: string = '';

  rules = {
    required: (value: string) => !!value || '',
  };

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          text: 'Status',
          value: 'statusId',
          filter: (value: number) => {
            if (this.selectedStatuses.length < 1 || this.selectedStatuses.includes(value)) {
              return true;
            } else {
              return false;
            }
          },
        },
        {
          text: 'Client',
          value: 'customerCode',
        },
        {
          text: 'Vehicle',
          value: 'vehicleCodes',
        },
        {
          text: '#',
          value: 'id',
        },
        {
          sortable: false,
          text: 'Order Num',
          value: 'orderNumber',
        },
        {
          text: 'Date',
          value: 'loadDate',
        },
        {
          text: 'From',
          value: 'from',
        },
        {
          text: 'To',
          value: 'to',
        },
        {
          sortable: false,
          text: 'Final Price',
          value: 'finalPrice',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    customFilter(column: string, search: string, item: Load) {
      let result: boolean = false;
      
      if (search === '') {
        result = true;
      } else {
        result = item.statusName.toLowerCase().includes(search.toLowerCase()) ||
          ('' + item.id).includes(search) ||
          item.customerCode.toLowerCase().includes(search.toLowerCase()) ||
          item.from.toLowerCase().includes(search.toLowerCase()) ||
          item.to.toLowerCase().includes(search.toLowerCase()) ||
          (item.loadDate ? item.loadDate.toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.orderNumber ? item.orderNumber.toLowerCase().includes(search.toLowerCase()) : false) ||
          item.operatorName.toLowerCase().includes(search.toLowerCase()) ||
          (item.horseVehicleCode ? item.horseVehicleCode.toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.trailer1VehicleCode ? item.trailer1VehicleCode.toLowerCase().includes(search.toLowerCase()) : false) ||
          (item.trailer2VehicleCode ? item.trailer2VehicleCode.toLowerCase().includes(search.toLowerCase()) : false);
      }

      return result;
    }

    customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
      // If the result is negative, a is sorted before b.
      // If the result is positive, b is sorted before a.
      // If the result is 0, no changes are done with the sort order of the two values.

      switch (sortBy[0]) {
        case 'statusId':
          // statusId is a number
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => b.statusId - a.statusId);            
          } else {
            items.sort((a: Load, b: Load) => a.statusId - b.statusId);
          }
          break;

        case 'customerCode':
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => b.customerCode.toLowerCase().localeCompare(a.customerCode.toLowerCase()));
          } else {
            items.sort((a: Load, b: Load) => a.customerCode.toLowerCase().localeCompare(b.customerCode.toLowerCase()));
          }
          break;

        case 'vehicleCodes':
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => (a.horseVehicleCode ? a.horseVehicleCode : '').toLowerCase().localeCompare((b.horseVehicleCode ? b.horseVehicleCode : '').toLowerCase()));
          } else {
            items.sort((a: Load, b: Load) => (b.horseVehicleCode ? b.horseVehicleCode : '').toLowerCase().localeCompare((a.horseVehicleCode ? a.horseVehicleCode : '').toLowerCase()));
          }
          break;

        case 'id':
          // id is a number
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => b.id - a.id);            
          } else {
            items.sort((a: Load, b: Load) => a.id - b.id);
          }
          break;

        case 'loadDate':
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => (a.loadDate ? a.loadDate : '').toLowerCase().localeCompare((b.loadDate ? b.loadDate : '').toLowerCase()));
          } else {
            items.sort((a: Load, b: Load) => (b.loadDate ? b.loadDate : '').toLowerCase().localeCompare((a.loadDate ? a.loadDate : '').toLowerCase()));
          }
          break;

        case 'from':
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => b.from.localeCompare(a.from));
          } else {
            items.sort((a: Load, b: Load) => a.from.localeCompare(b.from));
          }
          break;

        case 'to':
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => b.to.localeCompare(a.to));
          } else {
            items.sort((a: Load, b: Load) => a.to.localeCompare(b.to));
          }
          break;
      }

      return items;
    }

    async beforeMount() {
      await AppService.listStatuses(false);
      await UserService.listTypes(false);
      await LoadExtraService.listExtraTypes(false);
      
      await this.listApprovalLoads();

      // check if logged in User is a Super User with Clearance 2 or below, if so, pre-select Top 3 statuses
      if (this.$store.getters['users/loggedInUser'].typeId === 1 && this.$store.getters['users/loggedInUser'].clearanceLevel <= 2) {
        this.selectedStatuses.push(this.$store.getters['app/statusHelper'].waitOrderNumber);
        this.selectedStatuses.push(this.$store.getters['app/statusHelper'].waitOpprove);
        this.selectedStatuses.push(this.$store.getters['app/statusHelper'].rejected);
      }

      // #127 check if logged in User is an Operator, if so, pre-select Top 3 statuses
      if (this.$store.getters['users/loggedInUser'].typeId === 4) {
        this.selectedStatuses.push(this.$store.getters['app/statusHelper'].waitOrderNumber);
        this.selectedStatuses.push(this.$store.getters['app/statusHelper'].waitOpprove);
        this.selectedStatuses.push(this.$store.getters['app/statusHelper'].rejected);
      }

      // check if logged in User is an Administrator, if so, pre-select these statuses
      if (this.$store.getters['users/loggedInUser'].typeId === 3) {
        this.selectedStatuses.push(this.$store.getters['app/statusHelper'].rejected);
        this.selectedStatuses.push(this.$store.getters['app/statusHelper'].waitPodScan);
        this.selectedStatuses.push(this.$store.getters['app/statusHelper'].scanned);
      }
    }

    async refresh() {
      // reset store to empty
      this.$store.dispatch('loads/setApprovalLoads', {
        loads: [],
      });
      await this.listApprovalLoads();
    }

    async listApprovalLoads() {
      try {
        this.isLoading = true;
        const response: string = await LoadService.listApprovalLoads(true);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    getSelectionIcon(statusId: number) {
      if (this.selectedStatuses.includes(statusId)) {
        // show selected tickbox
        return 'mdi-checkbox-marked';
      } else {
        // show empty tickbox
        return 'mdi-checkbox-blank-outline';
      }
    }

    toggle(statusId: number) {
      this.$nextTick(() => {
        if (this.selectedStatuses.includes(statusId)) {
          // remove this one
          this.selectedStatuses.splice(this.selectedStatuses.indexOf(statusId), 1);
        } else {
          //add this one
          this.selectedStatuses.push(statusId);
        }
      })
    }

    async listExtraSummary(item: Load) {
      try {
        // always check for all the comments in case someone else added one in the background - until we are using websockets
        const response = await LoadExtraService.listExtraSummary({params: {loadId: item.id}});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadExtraSummary = [];
          } else {
           this.loadExtraSummary = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.loadExtraSummary = [];
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Approvals.vue - listExtraSummary - 877 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async listLoadEvents(item: Load) {
      try {
        // always check for all the comments in case someone else added one in the background - until we are using websockets
        const response = await LoadEventService.listByLoad({params: {loadId: item.id}});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadEvents = [];
          } else {
           this.loadEvents = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.loadEvents = [];
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Approvals.vue - listLoadEvents - 877 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async expandLoad(item: Load, expand: any) {
      this.loadEvents = [];
      // go fetch the load events for this load
      try {
        this.expandDelay = true;
        await this.listExtraSummary(item);
        await this.listLoadEvents(item);
      } finally {
        this.expandDelay = false;
      }

      expand(true);
    }

    closeRow(expand: any) {
      expand(false);
    }

    getFormattedLoadDate(loadDate: string) {
      return dayjs(loadDate).format('ddd DD MMM YYYY');
    }

    viewLoadDetails(item: Load) {
      this.$router.push({name: 'View Load', params: {id: this.hashids.encode(item.id)}});
    }

    editLoadDetails(item: Load) {
      this.$router.push({name: 'Edit Load', params: {id: this.hashids.encode(item.id)}});
    }

    allowUpdateOrderNumberFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 2},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowApproveFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 2},
      ]);
    }
    
    allowExportFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 1},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 2},
      ]);
    }

    allowRejectFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 2},
      ]);
    }
  // #endregion
  
  // #region Functions required for the CSV Download
    getDataForCSV() {
      const csvApprovalLoads: Load[] = Array.from(this.$store.getters['loads/approvalLoads']);
      csvApprovalLoads.sort((a, b) => (a.statusName.toUpperCase() > b.statusName.toUpperCase()) ? 1 : -1 );
      return csvApprovalLoads;
    }
    
    getFieldsForCSV() {
      return {
        'Status': 'statusName',
        'Operator': 'operatorName',
        'Truck': 'horseVehicleCode',
        'Load Number': 'id',
        'Client': 'customerCode',
        'From': 'from',
        'To': 'to',
        'Load Date': 'loadDate',
        'Rate': 'rate',
        'Driver': 'driverNickname',        
        'Trailer 1': 'trailer1VehicleCode',
        'Trailer 2': 'trailer2VehicleCode',
      };
    }
  // #endregion

  // #region Functions required for the Dialog to Update Order Number
    launchUpdateOrderNumberDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);
      this.newOrderNumber = '';
      this.showOrderNumberDialog = true;
    }

    async doUpdateOrderNumber() {
      try {
        this.orderNumberClicked = true;

        const response = await LoadService.updateOrderNumber({ 
          id: this.clickedItem.id, 
          orderNumber: this.newOrderNumber 
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listApprovalLoads();
            this.showOrderNumberDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Approvals.vue - doUpdateOrderNumber - 1100 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.orderNumberClicked = false;
      }
    } 
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Approve Load
    launchApproveLoadDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);
      this.action = 'APPROVE'; 
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #71BC78">approve</span> Load #' + this.clickedItem.id + '.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    launchRejectLoadDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);
      this.rejectionReason = '';

      this.action = 'REJECT'; 

      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212">reject</span> Load #' + this.clickedItem.id + '.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'APPROVE') {
        this.doApproveLoad();
      } else if (this.action === 'REJECT') {
        this.doRejectLoad();
      }
    }

    async doApproveLoad() {
      try {
        this.confirmActionDelay = true;

        const response = await LoadService.approve({ id: this.clickedItem.id });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listApprovalLoads();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Approvals.vue - doApproveLoad - 1222 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.confirmActionDelay = false;
      }
    }

    async doRejectLoad() {
      try {
        this.confirmActionDelay = true;

        const response = await LoadService.reject({ 
          loadId: this.clickedItem.id,
          comment: this.rejectionReason,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listApprovalLoads();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Approvals.vue - doRejectLoad - 1257 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.confirmActionDelay = true;
      }
    }
  // #endregion
}
